import {
  BarChart3,
  CheckSquare,
  ClipboardList,
  History,
  Landmark,
  LayoutDashboard,
  ListTodo,
  Package,
  Printer,
  Settings,
  ShoppingCart,
  Sliders,
  Trash2,
  Truck,
  Users,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { permissionForPath } from '@klothiq/domain';

export interface NavigationItem {
  label: string;
  path: string;
  icon: LucideIcon;
  group: string;
  roles?: readonly string[];
}

// Original destinations/role restrictions. Display groups do not grant access.
export const NAV_ITEMS: readonly NavigationItem[] = [
  {
    label: 'Users & Access',
    path: '/settings/users',
    icon: Users,
    group: 'admin',
    roles: ['owner'],
  },
  { label: 'Dashboard', path: '/', icon: LayoutDashboard, group: 'main' },
  { label: 'Products', path: '/products', icon: Package, group: 'stock' },
  { label: 'Inventory', path: '/inventory', icon: Warehouse, group: 'stock' },
  { label: 'Suppliers', path: '/suppliers', icon: Truck, group: 'stock' },
  { label: 'Purchases', path: '/purchases', icon: ClipboardList, group: 'stock' },
  { label: 'Customers', path: '/customers', icon: Users, group: 'sell' },
  { label: 'Sales', path: '/sales', icon: ShoppingCart, group: 'sell' },
  { label: 'Print templates', path: '/print-templates', icon: Printer, group: 'sell' },
  { label: 'Delivery', path: '/deliveries', icon: Truck, group: 'sell' },
  { label: 'Accounts', path: '/accounts', icon: Landmark, group: 'business' },
  {
    label: 'Plans & Tasks',
    path: '/plans',
    icon: CheckSquare,
    group: 'workspace',
    roles: ['owner', 'admin'],
  },
  { label: 'My Tasks', path: '/my-tasks', icon: ListTodo, group: 'workspace' },
  { label: 'My Sales', path: '/my-sales', icon: ShoppingCart, group: 'workspace' },
  { label: 'Team Sales', path: '/team-sales', icon: BarChart3, group: 'business' },
  { label: 'Reports', path: '/reports', icon: BarChart3, group: 'business' },
  {
    label: 'Master Config',
    path: '/master-config',
    icon: Sliders,
    group: 'admin',
    roles: ['owner', 'admin'],
  },
  {
    label: 'Audit History',
    path: '/audit',
    icon: History,
    group: 'admin',
    roles: ['owner', 'admin'],
  },
  {
    label: 'Recycle Bin',
    path: '/recycle-bin',
    icon: Trash2,
    group: 'admin',
    roles: ['owner', 'admin'],
  },
  {
    label: 'Data Migration',
    path: '/settings/import',
    icon: Settings,
    group: 'admin',
    roles: ['owner', 'admin'],
  },
  { label: 'My Account', path: '/my-account', icon: Settings, group: 'account' },
];

export const NAV_GROUPS = [
  { id: 'main', label: 'Main', paths: ['/'] },
  {
    id: 'sell',
    label: 'Sell & deliver',
    paths: ['/sales', '/customers', '/deliveries', '/print-templates'],
  },
  {
    id: 'stock',
    label: 'Products & stock',
    paths: ['/products', '/inventory', '/suppliers', '/purchases'],
  },
  { id: 'business', label: 'Business', paths: ['/accounts', '/reports', '/team-sales'] },
  { id: 'workspace', label: 'My workspace', paths: ['/my-tasks', '/my-sales', '/plans'] },
  {
    id: 'admin',
    label: 'Administration',
    paths: ['/settings/users', '/master-config', '/audit', '/recycle-bin', '/settings/import'],
  },
  { id: 'account', label: 'Account', paths: ['/my-account'] },
] as const;

export function availableNavigation(
  role: string | null | undefined,
  can: (permission: string) => boolean,
) {
  return NAV_ITEMS.filter((item) => {
    const permission = permissionForPath(item.path);
    return (
      (!item.roles || (!!role && item.roles.includes(role))) && (!permission || can(permission))
    );
  });
}

export function currentNavigation(pathname: string, items: readonly NavigationItem[]) {
  return [...items]
    .sort((a, b) => b.path.length - a.path.length)
    .find(
      (item) =>
        pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path + '/')),
    );
}

export function navigationGroups(items: readonly NavigationItem[], search = '') {
  const term = search.trim().toLocaleLowerCase();
  return NAV_GROUPS.filter((g) => term || g.id !== 'account')
    .map((group) => ({
      ...group,
      items: group.paths.flatMap((path) => {
        const item = items.find((item) => item.path === path);
        return item && (!term || `${item.label} ${group.label}`.toLocaleLowerCase().includes(term))
          ? [item]
          : [];
      }),
    }))
    .filter((group) => group.items.length > 0);
}
